import { FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useCoachOverview,
  useInviteMutation,
  useInviteActionMutation,
  useAddClientToFamily,
  useUnreadMessages,
  CoachOverviewClient,
} from "../lib/queries";
import { LoadingState, SkeletonList, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";
import { Modal } from "../components/Modal";
import { TourPrompt } from "../components/TourPrompt";
import { OpenFlags } from "../components/OpenFlags";
import { PlanChoiceFields, PlanChoice } from "../components/PlanChoiceFields";
import { SubscriptionState } from "../components/SubscriptionState";
import { initials } from "../lib/initials";

type FilterChip = "all" | "no-plan" | "inactive";

/**
 * Whether a client is still someone the coach is working with.
 *
 * Derived, never stored: a lapsed subscription is a billing fact read live from the family's account,
 * and a coach-set stop is on the athlete. Two sources, one question -- and storing the answer would make
 * a third that could disagree with both.
 */
function isInactive(client: CoachOverviewClient): boolean {
  return client.status === "inactive" || client.billing_status === "cancelled";
}

type InviteType = "minor" | "adult";

/**
 * "Intermediate · age 12", or whichever half is known. Returns empty when neither is, so a client
 * created before these were carried through doesn't render a stray empty badge.
 */
function describeClient(client: { age: number | null; level: string | null }): string {
  return [client.level, client.age !== null ? `age ${client.age}` : null].filter(Boolean).join(" · ");
}

/** What the coach should actually do about it, since the badge alone only says something went wrong. */
function deliveryFailureHint(status: "bounced" | "complained"): string {
  return status === "bounced"
    ? "The email address rejected this invite. Check it for typos, then cancel and re-invite with the corrected address."
    : "The recipient marked this invite as spam. Reach out another way before sending again.";
}

export function CoachDashboard() {
  // One request for the whole working set, rather than a fetch per screen. See useCoachOverview.
  const overviewQuery = useCoachOverview();
  const sendInvite = useInviteMutation();
  const addToFamily = useAddClientToFamily();
  // Which clients have something waiting. Shared cache with the nav dot, so the page adds no request.
  const unread = useUnreadMessages();
  const unreadClientIds = new Set(unread.data?.client_ids ?? []);
  const resendInvite = useInviteActionMutation("resend");
  const cancelInvite = useInviteActionMutation("cancel");
  /** The invite awaiting confirmation before it takes an athlete off the roster -- see F84. */
  const [confirmingCancel, setConfirmingCancel] = useState<{ inviteId: string; name: string } | null>(null);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterChip>("all");

  const [showInviteForm, setShowInviteForm] = useState(false);
  /**
   * Whether the dialog is inviting a new family or adding an athlete to one that already exists
   * (finding #15). Two jobs, one entry point: from the coach's side both are "a new athlete starts
   * training with me", and splitting them into two buttons would make the roster page ask a question
   * before the coach has said what they want to do.
   */
  const [addMode, setAddMode] = useState<"invite" | "existing">("invite");
  const [existingGuardianEmail, setExistingGuardianEmail] = useState("");
  const [inviteType, setInviteType] = useState<InviteType>("adult");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [athleteName, setAthleteName] = useState("");
  const [athleteAge, setAthleteAge] = useState("");
  const [athleteLevel, setAthleteLevel] = useState("");
  /** Which plan they start on and what else their family may see -- the coach's call (item 12). */
  const [planChoice, setPlanChoice] = useState<PlanChoice | null>(null);

  const clients = overviewQuery.data?.clients;

  /**
   * The families already on the roster, by the email the coach knows them by. Built from the clients
   * they can already see, so the picker can never name somebody else's family.
   */
  const knownFamilies = Array.from(
    new Map(
      (clients ?? [])
        .filter((client) => client.guardian_email && client.status !== "invited")
        .map((client) => [client.guardian_email as string, client.name])
    ).entries()
  ).sort(([a], [b]) => a.localeCompare(b));
  /**
   * Invites by id, so an invited client can reach the invite that produced it.
   *
   * The invite is no longer rendered as its own row -- the client is -- but it still owns the things
   * only an invite has: the contact address the email went to, whether that email bounced, and the
   * resend/cancel actions.
   */
  const inviteById = useMemo(
    () => new Map((overviewQuery.data?.invites ?? []).map((invite) => [invite.id, invite])),
    [overviewQuery.data]
  );

  const filteredClients = useMemo(() => {
    if (!clients) return [];
    return clients
      .filter((c) => (filter === "no-plan" ? !c.has_published_plan : true))
      /**
       * Inactive clients are hidden unless you ask for them.
       *
       * They are the ones you are not working with, so they do not belong in a list whose job is "who
       * needs something from me" -- but they are never deleted, and one chip brings them back. The
       * "No plan" filter deliberately hides them too: an athlete you have stopped coaching having no
       * plan is not a problem to fix.
       */
      .filter((c) => (filter === "inactive" ? isInactive(c) : !isInactive(c)))
      .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  }, [clients, search, filter]);

  const inactiveCount = (clients ?? []).filter(isInactive).length;

  async function handleSendInvite(event: FormEvent) {
    event.preventDefault();
    // For an adult self-managed invite there's no separate "athlete name" field (the contact IS the
    // athlete) -- athleteName only ever gets typed into its own input for a minor invite, so it must
    // fall back to contactName here rather than requiring a value that adult invites never collect.
    const resolvedAthleteName = inviteType === "adult" ? contactName : athleteName;
    if (!contactName.trim() || !contactEmail.trim() || !resolvedAthleteName.trim()) return;

    await sendInvite.mutateAsync({
      inviteType,
      contactName: contactName.trim(),
      contactEmail: contactEmail.trim(),
      athleteName: resolvedAthleteName.trim(),
      athleteAge: athleteAge ? Number(athleteAge) : undefined,
      athleteLevel: athleteLevel.trim() || undefined,
      ...(planChoice ?? {}),
    });

    setContactName("");
    setContactEmail("");
    setAthleteName("");
    setAthleteAge("");
    setAthleteLevel("");
    setShowInviteForm(false);
  }

  function handleAddToFamily(event: FormEvent) {
    event.preventDefault();
    if (!existingGuardianEmail || !athleteName.trim()) return;

    // mutate with an onSuccess rather than await mutateAsync: a rejected add is already rendered from
    // the mutation's own error state, and awaiting it here would additionally throw out of the submit
    // handler as an unhandled rejection.
    addToFamily.mutate(
      {
        guardianEmail: existingGuardianEmail,
        name: athleteName.trim(),
        age: athleteAge ? Number(athleteAge) : undefined,
        level: athleteLevel.trim() || undefined,
        ...(planChoice ?? {}),
      },
      {
        onSuccess: () => {
          setExistingGuardianEmail("");
          setAthleteName("");
          setAthleteAge("");
          setAthleteLevel("");
          setShowInviteForm(false);
        },
      }
    );
  }

  // Split by where the action lives. The invite form is inside a modal, so its error has to render
  // inside that modal -- on the page behind it, it sat underneath the dialog and was invisible.
  const rosterError = resendInvite.error ?? cancelInvite.error;

  return (
    // No Library link or Sign out here any more -- both live in the persistent nav bar, which is on
    // every signed-in screen rather than only on the ones that remembered to add them.
    <PageShell
      title="Clients"
      // In the header rather than a full-width block above the list: the roster is what the page is
      // for, and a large button sitting between the title and the content pushed it down for no reason.
      actions={
        <button type="button" onClick={() => setShowInviteForm(true)}>
          Add an athlete
        </button>
      }
    >
      <TourPrompt />

      {/* Above the roster on purpose. An athlete reporting pain used to be findable only by opening the
          client and then the session it happened in -- which meant, in practice, not findable (F72). */}
      <OpenFlags clients={overviewQuery.data?.clients ?? []} />

      {rosterError && <ErrorState error={rosterError} />}
      {overviewQuery.isError && <ErrorState error={overviewQuery.error} onRetry={() => overviewQuery.refetch()} />}

      {showInviteForm && (
        <Modal title="Add an athlete" onClose={() => setShowInviteForm(false)}>
        <p className="form-label">Who is this</p>
        <div className="filter-chips" role="group" aria-label="How to add">
          <button
            type="button"
            className={addMode === "invite" ? "chip active" : "chip"}
            onClick={() => setAddMode("invite")}
          >
            A new family
          </button>
          <button
            type="button"
            className={addMode === "existing" ? "chip active" : "chip"}
            onClick={() => setAddMode("existing")}
            disabled={knownFamilies.length === 0}
          >
            Another athlete in an existing family
          </button>
        </div>

        {addMode === "existing" ? (
          <form className="plan-form" onSubmit={handleAddToFamily}>
            {addToFamily.isError && <ErrorState error={addToFamily.error} />}
            <label htmlFor="existing-family">Family</label>
            <select
              id="existing-family"
              value={existingGuardianEmail}
              onChange={(e) => setExistingGuardianEmail(e.target.value)}
              required
            >
              <option value="">Choose a family…</option>
              {knownFamilies.map(([email, exampleAthlete]) => (
                <option key={email} value={email}>
                  {email} ({exampleAthlete})
                </option>
              ))}
            </select>

            <label htmlFor="existing-athlete-name">Athlete name</label>
            <input id="existing-athlete-name" value={athleteName} onChange={(e) => setAthleteName(e.target.value)} required />

            <label htmlFor="existing-athlete-age">Age (optional)</label>
            <input id="existing-athlete-age" type="number" value={athleteAge} onChange={(e) => setAthleteAge(e.target.value)} />

            <label htmlFor="existing-athlete-level">Level (optional)</label>
            <input id="existing-athlete-level" value={athleteLevel} onChange={(e) => setAthleteLevel(e.target.value)} />

            <PlanChoiceFields idPrefix="existing" onChange={setPlanChoice} />

            {/* Said before the button, not after the fact: another athlete is another subscription, and
                the family should hear that from their coach rather than from a receipt. */}
            <p className="form-hint">
              They&rsquo;ll appear on this roster straight away. Additional athletes in a family are billed
              separately — talk to the family about the cost before adding one.
            </p>

            <button type="submit" disabled={addToFamily.isPending}>
              {addToFamily.isPending ? "Adding…" : "Add athlete"}
            </button>
          </form>
        ) : (
        <form className="plan-form" onSubmit={handleSendInvite}>
          {/* Above the fields, inside the dialog: a validation failure names a field below it, so it
              has to be somewhere the coach is already looking. */}
          {sendInvite.isError && <ErrorState error={sendInvite.error} />}
          <p className="form-label">Client type</p>
          <div className="filter-chips" role="group" aria-label="Invite type">
            <button
              type="button"
              className={inviteType === "adult" ? "chip active" : "chip"}
              onClick={() => setInviteType("adult")}
            >
              Adult, self-managed
            </button>
            <button
              type="button"
              className={inviteType === "minor" ? "chip active" : "chip"}
              onClick={() => setInviteType("minor")}
            >
              Minor
            </button>
          </div>

          <label htmlFor="invite-contact-name">{inviteType === "minor" ? "Guardian name" : "Client name"}</label>
          <input id="invite-contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} required />

          <label htmlFor="invite-contact-email">{inviteType === "minor" ? "Guardian email" : "Client email"}</label>
          <input
            id="invite-contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
          />

          {inviteType === "minor" && (
            <>
              <label htmlFor="invite-athlete-name">Athlete name</label>
              <input id="invite-athlete-name" value={athleteName} onChange={(e) => setAthleteName(e.target.value)} required />
            </>
          )}

          <label htmlFor="invite-athlete-age">Age (optional)</label>
          <input id="invite-athlete-age" type="number" value={athleteAge} onChange={(e) => setAthleteAge(e.target.value)} />

          <label htmlFor="invite-athlete-level">Level (optional)</label>
          <input id="invite-athlete-level" value={athleteLevel} onChange={(e) => setAthleteLevel(e.target.value)} />

          <PlanChoiceFields idPrefix="invite" onChange={setPlanChoice} />

          <button type="submit" disabled={sendInvite.isPending}>
            {sendInvite.isPending ? "Sending…" : "Send invite"}
          </button>
        </form>
        )}
        </Modal>
      )}

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search clients by name"
        />
        <div className="filter-chips" role="group" aria-label="Filter clients">
          <button className={filter === "all" ? "chip active" : "chip"} onClick={() => setFilter("all")}>
            All
          </button>
          <button className={filter === "no-plan" ? "chip active" : "chip"} onClick={() => setFilter("no-plan")}>
            No plan
          </button>
          {/* Only offered when there is something behind it -- an empty filter chip is a dead control. */}
          {inactiveCount > 0 && (
            <button className={filter === "inactive" ? "chip active" : "chip"} onClick={() => setFilter("inactive")}>
              Inactive ({inactiveCount})
            </button>
          )}
        </div>
      </div>

      {overviewQuery.isPending && (
        <>
          <LoadingState label="Loading your clients…" />
          <SkeletonList />
        </>
      )}

      {overviewQuery.isSuccess && clients && clients.length === 0 && (
        <p className="empty-state">No clients yet -- invite your first client to get started.</p>
      )}

      {overviewQuery.isSuccess && clients && clients.length > 0 && filteredClients.length === 0 && (
        <p className="empty-state">No clients match your search or filter.</p>
      )}

      {/*
        One list, not two. An invited athlete is now a real client from the moment the invite is sent,
        so they belong in the roster rather than in a separate "pending invites" strip above it -- and
        the coach can open them and build a plan while they wait. The invite has not disappeared: it is
        still what Resend and Cancel act on, reached through the client's invite_id.
      */}
      <ul className="client-list">
        {filteredClients.map((client) => {
          const invite = client.invite_id ? inviteById.get(client.invite_id) : undefined;
          const isInvited = client.status === "invited";
          return (
            // Three groups -- who, where they stand, what to do -- so that on a laptop each one starts at
            // the same place on every row instead of wherever the badges before it happened to end. On a
            // phone they stack in the same order.
            <li key={client.id} className="client-card client-row">
              <div className="client-who">
                <span className="client-avatar">{initials(client.name)}</span>
                <div className="client-who-text">
                  <span className="client-name">{client.name}</span>
                  {/* Facts about the athlete, as a line of text rather than a row of chips: none of these is
                      a status, and dressing them as one put them level with "Issue flagged". Level and age
                      were collected on the invite form and, until they appeared here, discarded. */}
                  <p className="client-meta">
                    {[describeClient(client), client.is_minor ? "Minor" : "Adult", isInvited ? invite?.contact_email : null]
                      .filter(Boolean)
                      .map((fact, i) => (
                        <span key={i}>
                          {i > 0 && " · "}
                          {fact}
                        </span>
                      ))}
                  </p>
                </div>
              </div>

              <div className="client-status">
                {isInvited &&
                  (invite?.delivery_status ? (
                    // A bounced invite looks identical to a pending one from the coach's side otherwise --
                    // they'd keep waiting on someone who never got the email.
                    <span className="badge badge-danger" title={deliveryFailureHint(invite.delivery_status)}>
                      {invite.delivery_status === "bounced" ? "Email bounced" : "Marked as spam"}
                    </span>
                  ) : (
                    <span className="badge badge-warn">Invite pending</span>
                  ))}

                <span className={client.has_published_plan ? "badge badge-ok" : "badge badge-warn"}>
                  {client.has_published_plan ? "Has plan" : "No plan yet"}
                </span>
                {/* Flagged issues surface on the list itself, not only inside the client -- the coach
                    shouldn't have to open every client to find out someone reported pain. */}
                {(client.sessions ?? []).some((s) => s.issue_flagged) && (
                  <span className="badge badge-danger">Issue flagged</span>
                )}
                {client.status === "inactive" && <span className="badge">Not coaching</span>}
                {/*
                  One component, shared with the client page, so the two can no longer disagree about
                  whether a family is leaving -- which they did, in opposite directions, at the same
                  moment (UX review F41). The roster cannot show the pending plan change because the
                  overview does not carry it; the client page passes it in, so the sequence is complete
                  exactly where the coach goes to understand it.
                */}
                <SubscriptionState client={client} />
              </div>

              <div className="client-actions">
                {/* Opens the client, not the plan builder. Going straight to plan editing meant there was
                    nowhere to see a client as a whole (PRD §5.2). Available while invited too -- building
                    the plan before they arrive is the point. */}
                <Link className="link-button primary" to={`/coach/clients/${client.id}`}>
                  Open
                </Link>
                {/* Messaging waits for someone to message: until the family signs up there is no account
                    on the other end, so the thread would be a monologue into an empty room. */}
                {!isInvited && (
                  <Link className="link-button secondary" to={`/coach/clients/${client.id}/messages`}>
                    Messages
                    {/* Which family is waiting, not just that somebody is -- the nav dot says there is
                        something; this says where. */}
                    {unreadClientIds.has(client.id) && <span className="nav-unread-dot" aria-label="Unread messages" />}
                  </Link>
                )}

                {/* Resend and Cancel stay on the card, but they no longer sit level with Open.
                    The card led with administrative chips and ended with a destructive button at the same
                    weight as everything else, so the row read as five equal choices when only one -- Open
                    -- is what a coach wants nine times out of ten (UX review F39). */}
                {isInvited && invite && (
                  <span className="invite-actions">
                    <button
                      type="button"
                      className="chip"
                      onClick={() => resendInvite.mutate(invite.id)}
                      disabled={resendInvite.isPending}
                    >
                      Resend
                    </button>
                    {/* Confirmed, because it is not what it sounds like. "Cancel invite" removes the
                        CLIENT: the athlete disappears from the roster in one click, along with any plan
                        already built for them, with no confirmation and no undo -- and it sits directly
                        beside Resend at chip size (UX review F84). */}
                    <button
                      type="button"
                      className="chip danger"
                      onClick={() => setConfirmingCancel({ inviteId: invite.id, name: client.name })}
                      disabled={cancelInvite.isPending}
                    >
                      Cancel invite
                    </button>
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {confirmingCancel && (
        <Modal title="Cancel this invitation?" onClose={() => setConfirmingCancel(null)}>
          <p className="form-hint">
            This removes <strong>{confirmingCancel.name}</strong> from your roster, along with any plan
            you have already built for them. They will not be notified, and this cannot be undone &mdash;
            you would have to invite them again from scratch.
          </p>
          {cancelInvite.isError && <ErrorState error={cancelInvite.error} />}
          <button
            type="button"
            className="danger"
            disabled={cancelInvite.isPending}
            onClick={() =>
              cancelInvite.mutate(confirmingCancel.inviteId, { onSuccess: () => setConfirmingCancel(null) })
            }
          >
            {cancelInvite.isPending ? "Cancelling…" : `Remove ${confirmingCancel.name}`}
          </button>
          <button type="button" className="chip" onClick={() => setConfirmingCancel(null)}>
            Keep them
          </button>
        </Modal>
      )}
    </PageShell>
  );
}
